import { useState, useEffect, useRef } from "react";
import { Button, Loader } from "@/common/components/ui";
import SignUpService from "@/services/NPS/Stepper/APIs/SignUpService";
import { notify } from "@/common/components/common/Notification";

const OtpVerification = ({
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [timer, setTimer] = useState(90);
  const [countryCode, setCountryCode] = useState<any>(null);
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const [error, setError] = useState("");
  const [otpError, setOtpError] = useState(""); // Separate error for OTP validation
  const [showOtpSection, setShowOtpSection] = useState(false); // Control visibility of OTP section
  const [isOtpButtonDisabled, setIsOtpButtonDisabled] = useState(false); // Disable OTP button after it's clicked
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [countryCodes, setCountryCodes] = useState<any[]>([]);
  const [codeReference, setCodeReference] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  // Fetch Country Codes
  useEffect(() => {
    const fetchCountryCodes = async () => {
      const res = await SignUpService.getCountryCodes();
      if (res && Array.isArray(res)) {
        setCountryCodes(res);
        const india = res.find(c => c.label === "IND");
        if (india) {
          setCountryCode(india.value);
          localStorage.setItem("countryCode", india.value);
        }
      }
    };
    fetchCountryCodes();
  }, []);

  // Handle Timer Countdown
  useEffect(() => {
    if (timer > 0 && showOtpSection) {
      const countdown = setTimeout(() => setTimer(t => t - 1), 1000);
      return () => clearTimeout(countdown);
    }
  }, [timer, showOtpSection]);

  // Validate Mobile Number
  const validateMobile = (mobileNumber: string): boolean => {
    // Indian mobile numbers should be exactly 10 digits and start with 6-9
    return /^[6-9]\d{9}$/.test(mobileNumber);
  };

  // Handle Mobile Number Change
  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ""); // Only allow numbers
    setMobile(value);

    // Validate and show error if invalid (only when number is complete - 10 digits)
    if (value.length === 10 && !validateMobile(value)) {
      setError("Please enter valid mobile number");
      // Hide OTP section if mobile becomes invalid
      setShowOtpSection(false);
      setIsOtpButtonDisabled(false);
    } else if (value.length === 10 && validateMobile(value)) {
      setError(""); // Clear error if valid
    } else if (value.length === 0) {
      setError("");
      setShowOtpSection(false);
      setIsOtpButtonDisabled(false);
    } else if (value.length < 10) {
      // Clear error while user is still typing (less than 10 digits)
      setError("");
      setShowOtpSection(false);
      setIsOtpButtonDisabled(false);
    }
  };

  // Handle OTP Send Request
  const handleSendOtp = async () => {
    if (!mobile) {
      setError("Please enter your mobile number first.");
      setShowOtpSection(false); // Hide OTP section
      return; // Stop if mobile number is empty
    }

    // Validate mobile number
    if (!validateMobile(mobile)) {
      setError("Please enter valid mobile number");
      setShowOtpSection(false); // Hide OTP section
      return; // Stop if mobile number is invalid
    }

    setError(""); // Clear previous error message

    try {
      const payload = { phone_number: mobile, phone_code: countryCode };
      localStorage.setItem("userMobile", mobile);

      const res = await SignUpService.sendOtp(payload);
      if (res?.code_reference) {
        setCodeReference(res.code_reference);
      }

      if (res?.message) {
        notify.success(res.message);
      } else {
        notify.info("OTP sent successfully!");
      }

      // Show OTP input section
      setShowOtpSection(true);
      setIsOtpButtonDisabled(true); // Disable OTP button after it's clicked
      setTimer(90); // Restart timer
      setOtpError(""); // Clear any previous OTP errors
    } catch (error) {
      notify.error("Failed to send OTP. Please try again.");
      console.error("OTP Send Error:", error);
      setShowOtpSection(false); // Hide OTP section on error
      setIsOtpButtonDisabled(false); // Re-enable OTP button
    }
  };

  const handleSendResetOtp = async () => {
    try {
      const payload = {
        code_reference: codeReference,
      };
      const res = await SignUpService.sendResetOtp(payload);
      notify.success(res?.message ?? "Reset OTP sent successfully!");

      setTimer(90);
      setOtpError(""); // Clear any previous OTP errors
    } catch (error) {
      notify.error("Failed to send reset OTP. Please try again.");
      console.error("Reset OTP Error:", error);
    }
  };

  // Handle OTP Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpCode = otp.join("");
    if (otpCode.length !== 6) {
      setOtpError("Please enter the complete 6-digit OTP.");
      return; // Stop if OTP is not complete
    }

    try {
      setIsLoading(true);
      setOtpError(""); // Clear previous OTP error
      const payload = { otp: otpCode, code_reference: codeReference };
      // const res: any;
      // if (isResetOtp) {
      //   res = await SignUpService.sendResetOtp(payload);
      // } else {
      const res = await SignUpService.sendOtp(payload);
      // }
      if (res) {
        notify.success("OTP Verified Successfully!");
        localStorage.setItem("accessToken", res.access);
        localStorage.setItem("refreshToken", res.refresh);
        localStorage.setItem("userInfo", JSON.stringify(res.user));
        onSuccess();
      } else {
        setOtpError("Invalid or expired OTP!!!");
        // notify.error("Invalid OTP. Please try again.");
      }
    } catch (error: any) {
      console.error("OTP Verification Error:", error);
      const errorMessage = error?.response?.data?.message ?? error?.message;
      setOtpError(errorMessage);
      // notify.error("Failed to verify OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Timer Formatting (MM:SS)
  const formattedTime = `${String(Math.floor(timer / 60)).padStart(2, "0")}:${String(timer % 60).padStart(2, "0")}`;

  return (
    <div className="w-full p-4 pt-10 md:p-4">
      <form onSubmit={handleSubmit} className="w-full space-y-3 md:space-y-3">
        {isLoading && <Loader message="Verifying your OTP..." />}
        <div className="font-sans text-[20px] font-bold text-[#1D2C56] md:text-[30px] md:text-[40px]">
          Login
        </div>

        {/* MOBILE INPUT */}
        <div className="flex gap-3">
          <div className="w-full">
            <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
              Mobile Number
              <span className="text-red-500">*</span>
            </label>
            <div className="mt-2 flex flex-wrap gap-2 md:flex-nowrap md:gap-3">
              <div className="flex w-full gap-3">
                {/* Country Code Dropdown */}
                <select
                  value={countryCode ?? ""}
                  onChange={e => setCountryCode(e.target.value)}
                  className="min-w-[70px] rounded-[6px] bg-[#f5f5f5] px-2 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] outline-none focus:border-[#66A0CD]"
                >
                  {countryCodes?.map(c => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>

                {/* Mobile Number Input */}
                <div className="flex-1">
                  <input
                    type="tel"
                    maxLength={10}
                    placeholder="Enter your Mobile Number"
                    value={mobile}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault(); // stop form submit
                        if (!isOtpButtonDisabled) {
                          handleSendOtp(); // trigger OTP
                        }
                      }
                    }}
                    onChange={handleMobileChange}
                    className={`w-full rounded-[6px] border bg-[#f5f5f5] px-4 py-2.5 font-sans text-[16px] font-bold text-[#2F363F] placeholder-[#2F363F] outline-none ${
                      error
                        ? "border-red-500 focus:border-red-500"
                        : "focus:border-[#66A0CD]"
                    }`}
                  />
                  {error && (
                    <p className="mt-2 font-sans text-[14px] font-semibold text-red-500">
                      {error}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* OTP Button */}
            <Button
              type="button"
              disabled={isOtpButtonDisabled}
              onClick={handleSendOtp}
              className={`mx-auto mt-5 block h-[50px] w-[180px] rounded-[6px] px-4 py-2 font-sans text-[16px] font-semibold tracking-[0.5px] text-white transition ${
                isOtpButtonDisabled
                  ? "cursor-not-allowed bg-gradient-to-l from-[#b3d8cf] to-[#8ebeb4]"
                  : "bg-gradient-to-l from-[#66A0CD] to-[#1D2C56] hover:opacity-90"
              }`}
            >
              OTP
            </Button>
          </div>
        </div>

        {/* OTP INPUTS SECTION (Visible after OTP button click) */}
        {showOtpSection && (
          <>
            <div>
              <p className="text-center font-sans text-[16px] font-semibold text-[#6A7378]">
                An OTP has been sent to your registered Mobile number.
              </p>
              <div className="mt-3 flex justify-center gap-3 md:gap-5">
                {Array.from({ length: 6 }).map((field, i) => (
                  <input
                    key={field?.toString() ?? i.toString()}
                    ref={el => (otpRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={otp[i] || ""}
                    onChange={e => {
                      const val = e.target.value.replace(/\D/g, "");
                      const next = [...otp];
                      next[i] = val.slice(-1);
                      setOtp(next);
                      // Clear OTP error when user starts typing
                      if (otpError) {
                        setOtpError("");
                      }
                      if (val && i < 5) {
                        otpRefs.current[i + 1]?.focus();
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key === "Backspace" && i > 0 && !otp[i]) {
                        otpRefs.current[i - 1]?.focus();
                      }
                    }}
                    className="text-20px h-10 w-10 rounded-[6px] bg-[#F5F5F5] text-center font-sans font-semibold text-[#1D2C56] outline-none focus:border-2 focus:border-[#66A0CD] md:h-14 md:w-14 md:text-[30px]"
                  />
                ))}
              </div>

              {otpError && (
                <p className="mt-2 text-center font-sans text-[14px] font-semibold text-red-500">
                  {otpError}
                </p>
              )}

              <p className="mt-3 text-center text-[16px] font-semibold text-[#000]">
                {timer > 0 ? (
                  <>
                    Resend in{" "}
                    <span className="font-semibold">{formattedTime}</span>
                  </>
                ) : (
                  <button
                    type="button"
                    className="cursor-pointer text-[16px] font-semibold text-[#66A0CD] hover:underline"
                    onClick={handleSendResetOtp}
                  >
                    Resend OTP
                  </button>
                )}
              </p>
            </div>

            {/* Submit Button */}
            <div className="mt-10 text-center">
              <Button
                type="submit"
                onClick={handleSubmit}
                className="m-auto h-[50px] w-[180px] rounded-md bg-gradient-to-r from-[#127B8D] to-[#10B981] py-2 font-sans text-[18px] font-semibold text-[#fff] transition hover:opacity-95"
              >
                Submit
              </Button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default OtpVerification;
